import type { FC } from "react";

import styled from "styled-components";

import { Badge } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { AverageToggle } from "app/pages/industry-analysis/players/table/bubble-chart/AverageToggle";
import { CHART_OPTIONS_META_DATA } from "app/pages/industry-analysis/players/table/bubble-chart/const";
import { ETypes } from "app/pages/industry-analysis/players/table/bubble-chart/types";
import { getTrackingFilters } from "app/pages/industry-analysis/players/table/bubble-chart/utils";
import { SwitcherGranularityContainer } from "app/pages/website-analysis/components/SwitcherGranularityContainer";
import { allTrackers } from "app/services/track/track";
import { FlexColumn } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { proNavigator } from "single-spa/react-app/ProNavigator";

interface IActionButtons {
    type: ETypes;
    showAverage: boolean;
    setShowAverage: (showAverage: boolean) => void;
    isDatasetEmpty: boolean;
    visibleChartTypes: ETypes[];
}

const Container = styled(FlexColumn)`
    padding-top: 16px;
    gap: 16px;
`;
export const ActionButtons: FC<IActionButtons> = (props) => {
    const { type, showAverage, setShowAverage, isDatasetEmpty, visibleChartTypes } = props;

    const onChartTypeClick = (clickedIndex: number) => {
        const nextType = visibleChartTypes[clickedIndex];
        if (nextType === undefined) {
            return;
        }
        const trackingFilters = getTrackingFilters(nextType, showAverage);
        allTrackers.dispatchTrackEvent("Button", "click", "QuadrantType", trackingFilters);
        void proNavigator.applyUpdateParamsAsync({
            bubbleChartDisplayType: ETypes[nextType],
        });
    };

    const chartTypeItems = visibleChartTypes.map((chartType) => {
        const { chartTypeItems: items } = CHART_OPTIONS_META_DATA[chartType];
        const { titleKey, tooltipKey } = items;
        const titleText = translate(titleKey);
        const title =
            chartType === ETypes.ConversionRate ? (
                <Flex
                    inline
                    alignItems="center"
                    gap="6px"
                >
                    {titleText}
                    <Badge
                        type="new"
                        data-automation="market-players-bubble-tab-conversion-new-badge"
                    />
                </Flex>
            ) : (
                titleText
            );
        return {
            title,
            tooltipText: translate(tooltipKey),
            dataAutomation: `market-players-bubble-chart-tab-${ETypes[chartType]}`,
        };
    });

    const selectedIndex = Math.max(0, visibleChartTypes.indexOf(type));

    return (
        <Container>
            <SwitcherGranularityContainer
                itemList={chartTypeItems}
                selectedIndex={selectedIndex}
                onItemClick={onChartTypeClick}
                customClass={"CircleSwitcher"}
            />
            {isDatasetEmpty ? null : (
                <AverageToggle
                    type={type}
                    showAverage={showAverage}
                    setShowAverage={setShowAverage}
                    showAvgUniqueUsers={type === ETypes.Loyalty}
                />
            )}
        </Container>
    );
};
